/**
 * Utilities for formatting rate values consistently across components
 */

import type { ColumnType, ExportConfig, RateType } from '../types/rate-shop-types';

/**
 * Formats a rate value based on the column type
 */
export function formatRateValue(value: number | null, columnType: ColumnType): string {
  if (value === null || value === undefined) return '-';

  if (columnType === 'price_index') {
    // Price index as decimal number with 2 decimal places
    return Number(value).toFixed(2);
  } else {
    // Currency formatting for client and competitor rates
    return `$${Number(value).toFixed(0)}`;
  }
}

/**
 * Truncates competitor name aggressively for header display
 * Aims for max 8-10 characters to match 5-day outlook styling
 */
export function truncateCompetitorName(name: string): string {
  if (!name) return name;

  const words = name.split(' ');

  // For single word, truncate if too long
  if (words.length === 1) {
    return words[0].length > 10 ? words[0].substring(0, 8) : words[0];
  }

  // For multiple words, be more aggressive
  const firstWord = words[0];
  const secondWord = words[1];

  // If first word is short, try to add second word
  if (firstWord.length <= 4 && secondWord) {
    const combined = `${firstWord} ${secondWord}`;
    if (combined.length <= 10) {
      return combined;
    }
    // Abbreviate second word
    return `${firstWord} ${secondWord.substring(0, 3)}`;
  }

  // If first word is long, just use abbreviated first word
  if (firstWord.length > 8) {
    return firstWord.substring(0, 8);
  }

  // Use first word only
  return firstWord;
}

/**
 * Gets first two words from competitor name for chart display
 * Used for chart legends to show "Best Western" instead of full name
 */
export function getFirstTwoWords(name: string): string {
  if (!name) return name;

  const words = name.split(' ');
  if (words.length <= 2) {
    return name; // Return as-is if 2 words or less
  }

  return words.slice(0, 2).join(' ');
}

/**
 * Generates CSV export filename
 */
export function generateExportFilename(config: ExportConfig): string {
  return `${config.brandCode}-hotel-rates-${config.rateType}-${config.date}.csv`;
}

/**
 * Formats rate type for display
 */
export function formatRateTypeDisplay(rateType: RateType): string {
  return rateType === 'best-flex' ? 'Best Flex Rate' : 'Lowest Rate';
}

/**
 * Formats chart tooltip value based on column type
 */
export function formatTooltipValue(value: number, columnType: ColumnType): string {
  if (columnType === 'price_index') {
    return Number(value).toFixed(2); // No $ for Price Index
  }
  return `$${value}`; // $ for rates
}
